import { useState } from "react";
import {
  startSignIn,
  completeSignIn,
  checkPasskeyAvailable,
  passkeySupported,
  signInWithPasskey,
  shouldOfferEnrollment,
  enrollPasskey,
  markDontAsk,
} from "../auth";
import { friendlyLoginError, friendlyPasskeyError } from "../auth/messages";
import { isDemoMode } from "../config";
import { DemoLogin } from "./DemoLogin";

interface Props {
  onSignedIn: () => void;
}

const COUNTRY_CODES = [
  { code: "+1", name: "US/Canada", flag: "🇺🇸" },
  { code: "+44", name: "UK", flag: "🇬🇧" },
  { code: "+61", name: "Australia", flag: "🇦🇺" },
  { code: "+64", name: "New Zealand", flag: "🇳🇿" },
];

/**
 * Single-page login with country code dropdown. Phone input reveals code field
 * after SMS is sent. Defaults to US (+1) for 10-digit entry.
 *
 * Passkeys (issue #43) layer on without changing the SMS default: after the
 * user submits their number we run a deferred "has a passkey?" check. If yes,
 * we offer biometric sign-in (no SMS) with a "use a code instead" escape hatch.
 * After a successful SMS login we offer, once per device, to enroll a passkey.
 */
export function LoginPage({ onSignedIn }: Props) {
  // Demo mode swaps the SMS flow for a role picker — no AWS in the loop.
  if (isDemoMode()) return <DemoLogin onSignedIn={onSignedIn} />;

  return <SmsLoginPage onSignedIn={onSignedIn} />;
}

function SmsLoginPage({ onSignedIn }: Props) {
  const [countryCode, setCountryCode] = useState("+1");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  // Passkey sign-in offered for this number (shown instead of the code field).
  const [passkeyOffer, setPasskeyOffer] = useState(false);
  // Enrollment offered after a successful SMS login (shown instead of the form).
  const [enrollOffer, setEnrollOffer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fadeOut, setFadeOut] = useState(false);

  const fullPhone = countryCode + phone.replace(/\D/g, "");

  /** Fade out, then hand off to the app. */
  const finish = () => {
    setFadeOut(true);
    setTimeout(() => onSignedIn(), 300);
  };

  // After a correct OTP: offer passkey enrollment once per device, else finish.
  const afterLogin = () => {
    if (shouldOfferEnrollment()) {
      setEnrollOffer(true);
      setBusy(false);
    } else {
      finish();
    }
  };

  const sendCode = async () => {
    setError(null);
    setBusy(true);
    try {
      await startSignIn(fullPhone);
      setPasskeyOffer(false);
      setCodeSent(true);
    } catch (err) {
      setError(friendlyLoginError(err));
    } finally {
      setBusy(false);
    }
  };

  // Phone submit: deferred passkey check first, then SMS as the fallback.
  const submitPhone = async () => {
    setError(null);
    setBusy(true);
    try {
      if (passkeySupported() && (await checkPasskeyAvailable(fullPhone))) {
        setPasskeyOffer(true);
        setBusy(false);
        return;
      }
    } catch {
      /* fall through to SMS — the check is best-effort */
    }
    await sendCode();
  };

  const usePasskey = async () => {
    setError(null);
    setBusy(true);
    try {
      await signInWithPasskey(fullPhone);
      finish();
    } catch (err) {
      // Stay on the offer so the user can retry or fall back to a code.
      setError(friendlyPasskeyError(err));
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    setError(null);
    setBusy(true);
    try {
      await completeSignIn(code);
      afterLogin();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed");
      setCode("");
      setBusy(false);
    }
  };

  const addPasskey = async () => {
    setError(null);
    setBusy(true);
    try {
      await enrollPasskey();
      finish();
    } catch (err) {
      // Enrollment is optional — never block the login it followed.
      setError(friendlyPasskeyError(err));
      finish();
    }
  };

  const notNow = () => {
    markDontAsk();
    finish();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!codeSent) {
      submitPhone();
    } else {
      verifyCode();
    }
  };

  const resetForm = () => {
    setCodeSent(false);
    setPasskeyOffer(false);
    setCode("");
    setError(null);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-slate-50">
      <div
        className={`w-full max-w-sm bg-white rounded-2xl shadow-sm p-6 transition-all duration-300 ${
          fadeOut ? "opacity-0 scale-95" : "opacity-100 scale-100"
        }`}
      >
        <h1 className="text-2xl font-bold text-slate-900">Hash Attendance</h1>

        {enrollOffer ? (
          <div className="mt-6 space-y-4">
            <div>
              <p className="text-base font-medium text-slate-900">
                Add a passkey to this device?
              </p>
              <p className="mt-1 text-sm text-slate-600">
                Next time you can sign in with Face ID, Touch ID, or your
                device PIN — no text message needed.
              </p>
            </div>
            <button
              type="button"
              onClick={addPasskey}
              disabled={busy}
              className="w-full h-12 rounded-lg bg-slate-900 text-white font-medium disabled:opacity-50"
            >
              {busy ? "Setting up…" : "Add passkey"}
            </button>
            <button
              type="button"
              onClick={notNow}
              disabled={busy}
              className="w-full h-12 rounded-lg bg-slate-100 text-slate-900 font-medium disabled:opacity-50"
            >
              Not now
            </button>
          </div>
        ) : passkeyOffer ? (
          <div className="mt-6 space-y-4">
            <div>
              <p className="text-base font-medium text-slate-900">
                Sign in with a passkey?
              </p>
              <p className="mt-1 text-sm text-slate-600">
                Use Face ID, Touch ID, or your device PIN for {fullPhone}.
              </p>
            </div>
            <button
              type="button"
              onClick={usePasskey}
              disabled={busy}
              className="w-full h-12 rounded-lg bg-slate-900 text-white font-medium disabled:opacity-50"
            >
              {busy ? "Waiting for passkey…" : "Sign in with a passkey"}
            </button>
            <button
              type="button"
              onClick={sendCode}
              disabled={busy}
              className="w-full h-12 rounded-lg bg-slate-100 text-slate-900 font-medium disabled:opacity-50"
            >
              Use a code instead
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <label className="block">
                <span className="text-sm font-medium text-slate-700">
                  Phone number
                </span>
                <div className="mt-1 flex gap-2">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    disabled={codeSent}
                    className="h-12 px-3 rounded-lg bg-slate-100 text-base focus:outline-none focus:ring-2 focus:ring-slate-900 disabled:opacity-50 transition-opacity duration-200"
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.flag} {c.code}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="555 123 4567"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={codeSent}
                    required
                    className="flex-1 h-12 px-4 rounded-lg bg-slate-100 text-base focus:outline-none focus:ring-2 focus:ring-slate-900 disabled:opacity-50 transition-opacity duration-200"
                  />
                </div>
              </label>

              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  codeSent ? "max-h-20 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <p className="text-sm text-slate-600 pt-2">
                  Code sent to {fullPhone}.{" "}
                  <button
                    type="button"
                    onClick={resetForm}
                    className="text-slate-900 underline"
                  >
                    Change number
                  </button>
                </p>
              </div>
            </div>

            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                codeSent ? "max-h-32 opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <label className="block">
                <span className="text-sm font-medium text-slate-700">
                  Verification code
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  placeholder="123456"
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  required={codeSent}
                  autoFocus={codeSent}
                  className="mt-1 w-full h-14 px-4 rounded-lg bg-white border-2 border-slate-200 text-2xl tracking-[0.5em] text-center focus:outline-none focus:border-slate-900 transition-colors"
                />
              </label>
            </div>

            <button
              type="submit"
              disabled={busy || !phone.trim() || (codeSent && code.length !== 6)}
              className="w-full h-12 rounded-lg bg-slate-900 text-white font-medium disabled:opacity-50"
            >
              {busy
                ? codeSent
                  ? "Verifying…"
                  : "Sending…"
                : codeSent
                ? "Sign in"
                : "Send code"}
            </button>
          </form>
        )}

        {error && (
          <p className="mt-4 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
